import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnInit,
} from '@angular/core';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import { Mandate, MobileProduct, Option } from '@dch/products-business-logic';
import { MpayService } from '../../../services/mpay.service';
import {
  Alert,
  AlertsService,
  ModalService,
} from '@proximus/ng-common-components';
import { UntypedFormControl, Validators } from '@angular/forms';
import { DeleteMandateComponent } from '../delete-mandate/delete-mandate.component';
import { WebshopUtils } from '../../../shared/webshop/webshop-utils';
import { OnDestroyHookDirective } from '../../../shared/hooks';
import {
  buildConfigurationLockKey,
  isPending,
  retrieveProductAndOptionFromConfirmationEvent,
} from '../../../shared/order-utils/order-utils';
import { OptionConfigurationLockService } from '../../../services/option-configuration-lock.service';

@Component({
  selector: 'mprd-mobile-payment',
  templateUrl: './mobile-payment.component.html',
})
export class MobilePaymentComponent
  extends OnDestroyHookDirective
  implements OnChanges, OnInit
{
  @Input() option: Option;
  @Input() selectedProduct: MobileProduct;

  public mandates: Mandate[];
  public hasError: boolean;
  public isThresholdUpdating: boolean;
  public mobileThreshold: number;
  public threshold;
  public missingThresholdInfo: boolean;
  public thresholdPlaceHolder: string;
  public thresholdRangeError: string;
  public isFormDisabled = true;
  public isPending = false;

  constructor(
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
    private translationService: TranslationService,
    private mpayService: MpayService,
    private alertService: AlertsService,
    private modalService: ModalService,
    private ngZone: NgZone,
    private optionConfigurationLockService: OptionConfigurationLockService,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToWebshopEvents();
    this.thresholdRangeError = this.translationService
      .getTranslatedValueFor('mpay.threshold.range.error')
      .replace('{0}', this.option.mcommerceThresholdMin)
      .replace('{1}', this.option.mcommerceThresholdMax);
    this.thresholdPlaceHolder = this.translationService
      .getTranslatedValueFor('mpay.threshold.placeholder')
      .replace('{0}', this.option.mcommerceThresholdMin)
      .replace('{1}', this.option.mcommerceThresholdMax);
    setTimeout(() => {
      this.inceptionService.forms(this.elementRef);
      this.inceptionService.init();
    });
  }

  ngOnChanges() {
    this.initInception();
    this.mandates = this.option.mandateResource.mandates;
    this.hasError = this.option.mandateResource.error;
    this.missingThresholdInfo =
      !this.isNumber(this.option.mcommerceThresholdMin) ||
      !this.isNumber(this.option.mcommerceThreshold) ||
      !this.isNumber(this.option.mcommerceThresholdMax);
    this.mobileThreshold = Number(this.option.mcommerceThreshold);
    this.threshold = new UntypedFormControl(this.mobileThreshold, [
      Validators.required,
      Validators.min(Number(this.option.mcommerceThresholdMin)),
      Validators.max(Number(this.option.mcommerceThresholdMax)),
    ]);
    if (this.isFormDisabled) {
      this.threshold.disable();
    }
  }

  initInception() {
    setTimeout(() => {
      this.inceptionService.panelAccordion(this.elementRef);
      this.inceptionService.init();
    });
  }

  getChargeFrequency(mandate: any): string {
    if (mandate.period && mandate.period.periodicity) {
      switch (mandate.period.periodicity) {
        case 'Hour':
          return this.translationService.getTranslatedValueFor(
            'mpay.threshold.frequency.hour',
          );
        case 'Day':
          return this.translationService.getTranslatedValueFor(
            'mpay.threshold.frequency.day',
          );
        case 'Week':
          return this.translationService.getTranslatedValueFor(
            'mpay.threshold.frequency.week',
          );
        case 'Month':
          return this.translationService.getTranslatedValueFor(
            'mpay.threshold.frequency.month',
          );
        case 'Year':
          return this.translationService.getTranslatedValueFor(
            'mpay.threshold.frequency.year',
          );
        default:
          return undefined;
      }
    }
    return undefined;
  }

  hasActiveMandates(): boolean {
    return this.mandates && this.mandates.length > 0;
  }

  updateMPayThreshold() {
    this.isThresholdUpdating = true;
    WebshopUtils.configureMpayThreshold(
      this.threshold.value,
      this.option.optionCodeForDescription,
      this.option.type,
      this.option.chargeConfiguration.event,
      this.option.chargeConfiguration.code,
    );
  }

  cancel() {
    this.threshold.reset();
    this.threshold.setValue(this.mobileThreshold);
  }

  deleteMandate(mandate: any) {
    this.modalService
      .create<DeleteMandateComponent>(DeleteMandateComponent)
      .subscribe((componentRef) => {
        componentRef.instance.mandate = mandate;
        componentRef.instance.accessNumber = this.selectedProduct.accessNumber;
        componentRef.instance.mpcUuid = this.option.optionCodeForDescription;
      });
  }

  private getAlert(
    type: 'error' | 'info' | 'success' | 'warning',
    alertKey: string,
    title: string,
    description: string,
  ): Alert {
    return {
      isClosable: true,
      alertKey: alertKey + 'alertLevel3',
      title,
      description,
      type,
    };
  }

  private isNumber(threshold: any) {
    return threshold && !isNaN(Number(threshold));
  }

  private subscribeToWebshopEvents() {
    WebshopUtils.subscribeToCartEvent((data: any) => {
      this.ngZone.run(() => {
        const pendingContextTagging = false;
        if (data.status === 'PENDING_ORDER') {
          this.isFormDisabled = true;
          this.threshold.disable();
        } else if (data.status === 'LOADED') {
          this.isFormDisabled = false;
          this.threshold.enable();
        }
        this.isPending = isPending(this.selectedProduct, this.option, false);
      });
    }, this.addSubscription);

    WebshopUtils.subscribeToConfirmationEvent((data: any) => {
      this.ngZone.run(() => {
        if (data.productDetails && data.action && data.action === 'configure') {
          const { currentOption } = retrieveProductAndOptionFromConfirmationEvent(
            this.selectedProduct,
            this.option.optionCodeForDescription,
            data.productDetails,
          );
          if (currentOption) {
            this.alertService.dispatchAlert(
              this.getAlert(
                'success',
                this.option.optionCodeForDescription,
                this.translationService.getTranslatedValueFor(
                  'message.confirmation.title',
                ),
                this.translationService.getTranslatedValueFor(
                  'mpay.threshold.update.success',
                ),
              ),
            );
            this.threshold.pristine = true;
            this.isPending = true;
            this.isThresholdUpdating = false;
            this.option.mcommerceThreshold = this.mobileThreshold;
            this.configurationLockOption();
          }
        }
      });
    }, this.addSubscription);

    WebshopUtils.subscribeToErrorEvent((data: any) => {
      this.ngZone.run(() => {
        if (data.products) {
          const currentOption = data.products.find(
            (p: any) => p.mpcUuid === this.option.optionCodeForDescription,
          );
          if (currentOption) {
            if (data.action && data.action === 'configure') {
              if (
                data.httpCode &&
                data.httpCode === 402 &&
                data.message &&
                data.message ===
                  'shoppingcart.ccw.validation.failed.customer.ibl'
              ) {
                this.alertService.dispatchAlert(
                  this.getAlert(
                    'error',
                    this.option.optionCodeForDescription,
                    this.translationService.getTranslatedValueFor(
                      'message.error.title',
                    ),
                    this.translationService.getTranslatedValueFor(
                      'message.error.description.cwc',
                    ),
                  ),
                );
              } else {
                this.alertService.dispatchAlert(
                  this.getAlert(
                    'error',
                    this.option.optionCodeForDescription,
                    this.translationService.getTranslatedValueFor(
                      'message.error.title',
                    ),
                    this.translationService.getTranslatedValueFor(
                      'mpay.threshold.update.error',
                    ),
                  ),
                );
              }
              this.cancel();
              this.isThresholdUpdating = false;
            }
          }
        }
      });
    }, this.addSubscription);
  }

  private configurationLockOption() {
    this.optionConfigurationLockService.configurationLockOption(
      this.option.optionCodeForDescription,
      this.selectedProduct.plc,
      this.selectedProduct.accessNumber,
    );
    sessionStorage.setItem(
      buildConfigurationLockKey(this.selectedProduct, this.option),
      Date.now().toString(),
    );
  }
}
